class CartService {

    constructor($http, $q, SnackbarService) {
        'ngInject';

        this.$http = $http;
        this.$q = $q;
        this.SnackbarService = SnackbarService;

        this.loaded = false;
        this.loading = false;
        this.showAllErrors = false;

        this.loadingInvoiceData = false;
        this.loadedInvoiceDataError = null;

        // Step 1
        this.items = [];
        this.summary = null;
        this.payments = null;
        this.paymentGroups = null;
        this.shipments = null
        this.shipmentOptions = {
            gls: null,
            paczkomaty: null
        };
        this.payment = '';
        this.orderId = null;
        this.userEmail = null;
        this.step = 1;
        this.userLogged = false;

        // Step 2
        this.data = {
            invoice: false,
            invoice_vat_ue: false,
            invoice_natural_person: false,
            comments: '',
            newsletter: false,
            shipmentAddressId: null,
            invoiceAddressId: null,
            acceptTerms: false,
            shipmentAddress: {
                companyName: '',
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                street: '',
                houseNumber: '',
                flatNumber: '',
                city: '',
                zipCode: '',
                country: ''
            },
            invoiceAddress: {
                firstName: '',
                lastName: '',
                companyName: '',
                taxNumber: '',
                street: '',
                houseNumber: '',
                flatNumber: '',
                city: '',
                zipCode: '',
                country: 'Polska'
            },
            gusInvoiceData: {
                companyName: null,
                address: null,
                zipCode: null,
                city: null,
                country: null,
                taxNumber: null
            }
        };

        this.addresses = {
            shipment: [],
            invoice: []
        };

        const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        this.dataValidations = {
            // adres do wysyłki
            shipmentAddressFirstName: () => this.data.shipmentAddress.firstName != '',
            shipmentAddressLastName: () => this.data.shipmentAddress.lastName != '',
            shipmentAddressEmail: () => emailRegex.test(String(this.data.shipmentAddress.email).toLowerCase()),
            shipmentAddressPhone: () => this.data.shipmentAddress.phone.length === 9,
            shipmentAddressStreet: () => (this.summary && !this.summary.shipping_data) || this.data.shipmentAddress.street != '',
            shipmentAddressHouseNumber: () => (this.summary && !this.summary.shipping_data) || this.data.shipmentAddress.houseNumber != '',
            shipmentAddressCity: () => (this.summary && !this.summary.shipping_data) || this.data.shipmentAddress.city != '',
            shipmentAddressZipCode: () => (this.summary && !this.summary.shipping_data) || this.data.shipmentAddress.zipCode != '',

            // faktura VAT EU na firmę
            invoiceAddressCompanyName: () => {
                if (this.data.invoice_vat_ue && this.data.invoiceAddress.companyName == '') {
                    this.dataValidationsMessages.invoiceAddressCompanyName = 'Nazwa firmy jest wymagana';

                    return false;
                }

                return true;
            },

            // faktura VAT na firmę lub faktura VAT EU na firmę lub płatność wymaga podania NIP
            invoiceAddressTaxNumber: () => {
                const taxEmpty = this.data.invoiceAddress.taxNumber == '';

                if (((this.data.invoice || this.data.invoice_vat_ue) && taxEmpty) || (this.summary && this.summary.payment_nip_required && taxEmpty)) {
                    this.dataValidationsMessages.invoiceAddressTaxNumber = this.data.invoice_vat_ue ? 'VAT EU jest wymagany' : 'NIP jest wymagany';

                    return false;
                }

                if (!this.data.invoice && !this.data.invoice_vat_ue && this.summary && !this.summary.payment_nip_required) {
                    return true;
                }

                this.data.invoiceAddress.taxNumber = String(this.data.invoiceAddress.taxNumber).toUpperCase().replace(/[^A-Z0-9]/gm, '');
                let taxNumber = this.data.invoiceAddress.taxNumber;

                // walidacja NIP PL
                if (this.data.invoice || (this.summary && this.summary.payment_nip_required)) {
                    if (taxNumber.length === 10 && parseInt(taxNumber, 10) > 0) {
                        let weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
                        let sum = 0;
                        for (let i = 0; i < 9; i++) {
                            sum += taxNumber[i] * weights[i];
                        }

                        return (sum % 11) === Number(taxNumber[9]);
                    }

                    this.dataValidationsMessages.invoiceAddressTaxNumber = 'Nieprawidłowy NIP';

                    return false;
                }

                // walidacja VAT EU
                if (this.data.invoice_vat_ue) {
                    const isValidEuVat = (vat) => {
                        if (!vat || vat.length < 4) return false;
                        const cc = vat.slice(0, 2); // kod kraju
                        const allowed = ['AT','BE','BG','HR','CY','CZ','DE','DK','EE','EL','GR','ES','FI','FR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','XI'];
                        if (!allowed.includes(cc)) return false;
                        // firma spoza PL (UE)
                        if (cc === 'PL') return false;

                        const body = vat.slice(2);
                        // 2-12 znaków alfanumerycznych
                        if (!/^[A-Z0-9]{2,12}$/.test(body)) return false;
                        if (/^([A-Z0-9])\1+$/.test(body)) return false;

                        return true;
                    };

                    if (!isValidEuVat(taxNumber)) {
                        this.dataValidationsMessages.invoiceAddressTaxNumber = 'Nieprawidłowy VAT EU';
                        return false;
                    }

                    return true;
                }

                this.dataValidationsMessages.invoiceAddressTaxNumber = 'Nieprawidłowy NIP';

                return false;
            },

            // faktura VAT na osobę fizyczną lub faktura VAT EU na firmę (bez imienia i nazwiska)
            invoiceAddressFirstName: () => !this.data.invoice_natural_person || this.data.invoiceAddress.firstName != '',
            invoiceAddressLastName: () => !this.data.invoice_natural_person || this.data.invoiceAddress.lastName != '',
            invoiceAddressStreet: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.street != '',
            invoiceAddressHouseNumber: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.houseNumber != '',
            invoiceAddressZipCode: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.zipCode != '',
            invoiceAddressCity: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.city != '',
        };

        this.loggedDataValidations = {
            // adres do wysyłki
            shipmentAddressId: () => this.data.shipmentAddressId,

            // faktura VAT EU na firmę
            invoiceAddressCompanyName: () => {
                if (this.data.invoice_vat_ue && this.data.invoiceAddress.companyName == '') {
                    this.loggedDataValidationsMessages.invoiceAddressCompanyName = 'Nazwa firmy jest wymagana';

                    return false;
                }

                return true;
            },

            // faktura VAT na firmę lub faktura VAT EU na firmę lub płatność wymaga podania NIP
            invoiceAddressTaxNumber: () => {
                const taxEmpty = this.data.invoiceAddress.taxNumber == '';

                if (((this.data.invoice || this.data.invoice_vat_ue) && taxEmpty) || (this.summary && this.summary.payment_nip_required && taxEmpty)) {
                    this.loggedDataValidationsMessages.invoiceAddressTaxNumber = this.data.invoice_vat_ue ? 'VAT EU jest wymagany' : 'NIP jest wymagany';

                    return false;
                }

                if (!this.data.invoice && !this.data.invoice_vat_ue && this.summary && !this.summary.payment_nip_required) {
                    return true;
                }

                this.data.invoiceAddress.taxNumber = String(this.data.invoiceAddress.taxNumber).toUpperCase().replace(/[^A-Z0-9]/gm, '');
                let taxNumber = this.data.invoiceAddress.taxNumber;

                // walidacja NIP PL
                if (this.data.invoice || (this.summary && this.summary.payment_nip_required)) {
                    if (taxNumber.length === 10 && parseInt(taxNumber, 10) > 0) {
                        let weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
                        let sum = 0;
                        for (let i = 0; i < 9; i++) {
                            sum += taxNumber[i] * weights[i];
                        }

                        return (sum % 11) === Number(taxNumber[9]);
                    }

                    this.loggedDataValidationsMessages.invoiceAddressTaxNumber = 'Nieprawidłowy NIP';

                    return false;
                }

                // walidacja VAT EU
                if (this.data.invoice_vat_ue) {
                    const isValidEuVat = (vat) => {
                        if (!vat || vat.length < 4) return false;
                        const cc = vat.slice(0, 2); // kod kraju
                        const allowed = ['AT','BE','BG','HR','CY','CZ','DE','DK','EE','EL','GR','ES','FI','FR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','XI'];
                        if (!allowed.includes(cc)) return false;
                        // firma spoza PL (UE)
                        if (cc === 'PL') return false;

                        const body = vat.slice(2);
                        // 2-12 znaków alfanumerycznych
                        if (!/^[A-Z0-9]{2,12}$/.test(body)) return false;
                        if (/^([A-Z0-9])\1+$/.test(body)) return false;

                        return true;
                    };

                    if (!isValidEuVat(taxNumber)) {
                        this.loggedDataValidationsMessages.invoiceAddressTaxNumber = 'Nieprawidłowy VAT EU';

                        return false;
                    }

                    return true;
                }

                this.loggedDataValidationsMessages.invoiceAddressTaxNumber = 'Nieprawidłowy NIP';

                return false;
            },

            // faktura VAT na osobę fizyczną lub faktura VAT EU na firmę (bez imienia i nazwiska)
            invoiceAddressFirstName: () => !this.data.invoice_natural_person || this.data.invoiceAddress.firstName != '',
            invoiceAddressLastName: () => !this.data.invoice_natural_person || this.data.invoiceAddress.lastName != '',
            invoiceAddressStreet: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.street != '',
            invoiceAddressHouseNumber: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.houseNumber != '',
            invoiceAddressZipCode: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.zipCode != '',
            invoiceAddressCity: () => !(this.data.invoice_natural_person || this.data.invoice_vat_ue) || this.data.invoiceAddress.city != '',
        };

        this.dataValidationsMessages = {}
        this.loggedDataValidationsMessages = {}
    }

    load() {
        const deferred = this.$q.defer();
        this.loading = true;

        this.$http.get('/ajax/checkout/get-cart-content').then(
            (response) => {
                const data = response.data.data;

                for (const product of data.products) {
                    product.savedQuantity = product.quantity;
                }

                this.items = data.products;
                this.summary = data.summary;
                this.loaded = true;
                this.loading = false;

                deferred.resolve({
                    items: this.items,
                    summary: this.summary
                });
            },
            (response) => {
                this.loading = false;
                this.loaded = true;

                if (response.status == 404) {
                    this.items = [];
                    this.summary = null;

                    deferred.resolve({
                        items: this.items,
                        summary: null
                    });

                    return;
                }

                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    loadPayments() {
        const deferred = this.$q.defer();
        this.loading = true;

        this.$http.get('/ajax/checkout/get-payments').then(
            (response) => {
                this.loading = false;
                const data = response.data.data;

                this.payments = data.payments;
                this.paymentGroups = data.paymentGroups;

                deferred.resolve({
                    payments: this.payments,
                    paymentGroups: this.paymentGroups
                });
            },
            (response) => {
                this.loading = false;

                if (response.status != 404)
                    this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    loadShipments() {
        if (!this.summary) return;

        const deferred = this.$q.defer();
        this.loading = true;

        this.$http.post(
            '/ajax/checkout/get-shipments',
            {
                country: this.summary.country
            }
        ).then(
            (response) => {
                this.loading = false;

                if (response.data.message && response.data.message.type != 'success') {

                    const message = response.data.message.content;

                    this.SnackbarService.add(message, 'error', true, 3000);

                    this.shipments = null;

                    deferred.reject(message);

                    return;
                }

                const data = response.data.data;

                this.shipments = data.shipments.map(shipment => {
                    shipment.options = {};
                    shipment.id = parseInt(shipment.id);

                    return shipment;
                });

                deferred.resolve({
                    shipments: this.shipments
                });
            },
            (response) => {
                this.loading = false;

                if (response.status != 404)
                    this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    loadAddresses() {
        const deferred = this.$q.defer();
        this.loading = true;

        let count = 0;

        this.$http.get('/ajax/checkout/get-shipment-addresses').then(
            (response) => {
                count++;
                this.addresses.shipment = response.data.data.addresses;

                if (count < 2) return;

                this.loading = false;
                deferred.resolve(this.addresses);
            },
            (response) => {
                this.loading = false;

                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        this.$http.get('/ajax/checkout/get-invoice-addresses').then(
            (response) => {
                count++;
                this.addresses.invoice = response.data.data.addresses;

                if (count < 2) return;

                this.loading = false;
                deferred.resolve(this.addresses);
            },
            (response) => {
                this.loading = false;

                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    changeQuantity(item) {
        if (!this.loaded) return;

        const deferred = this.$q.defer();
        this.loading = true;

        this.$http.post(
            '/ajax/checkout/change-product-quantity',
            {
                productId: item.product_id,
                variantId: item.variant_id || null,
                quantity: item.quantity
            }
        ).then(
            (response) => {
                this.loading = false;

                if (response.data.message && response.data.message.type != 'success') {

                    const message = response.data.message.content;

                    this.SnackbarService.add(message, 'error', true, 3000);

                    for (const item of this.items) {
                        item.quantity = item.savedQuantity;
                    }

                    deferred.reject(message);

                    return;
                }

                const data = response.data.data;

                this.items = data.products;

                for (const product of this.items) {
                    product.savedQuantity = product.quantity;
                }

                this.summary = data.summary;

                deferred.resolve({
                    items: this.items,
                    summary: this.summary
                });
            },
            (response) => {
                for (const item of this.items) {
                    item.quantity = item.savedQuantity;
                }

                this.loading = false;

                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    removeItem(item) {
        if (!this.loaded) {
            return;
        }

        const deferred = this.$q.defer();
        this.loading = true;

        const saveCopy = this.items;

        this.items = this.items.filter(cartItem => item != cartItem);

        this.$http.post(
            '/ajax/checkout/delete-product',
            {
                productId: item.product_id,
                variantId: item.variant_id || null
            }
        ).then(
            (response) => {
                this.loading = false;

                if (response.data.message && response.data.message.type != 'success') {

                    const message = response.data.message.content;

                    this.SnackbarService.add(message, 'error', true, 3000);

                    this.items = saveCopy;

                    deferred.reject(message);

                    return;
                }

                const message = response.data.message.content;

                const data = response.data.data;

                this.items = data.products;
                this.summary = data.summary;

                deferred.resolve({
                    items: this.items,
                    summary: this.summary
                });

                this.SnackbarService.add(message, 'success', true, 3000);

                if (typeof window.gtag === 'undefined') {
                    return;
                }

                // Fallback to product/variant ID
                let productCode = item.product_id;

                if (item.variant_id) {
                    productCode = item.variant_id;
                }

                if (item.product_code) {
                    productCode = item.product_code;
                }

                console.log(`gtag::event::remove_from_cart -> usunięcie -> ${productCode}`);

                let productPrice = item.price_gros.toString();
                const productName = item.name + (item.variant_id ? ' - ' + item.variant_name : '');
                const productCategoryName = item.category_name;
                const productQuantity = parseInt(item.quantity);

                window.gtag('event', 'remove_from_cart', {
                    "value": parseFloat(productPrice.replace(/(\d+),(\d+).*/, '$1.$2')),
                    "currency": "PLN",
                    "items": [
                        {
                            "id": productCode,
                            "name": productName,
                            "category": productCategoryName,
                            "quantity": productQuantity,
                            "price": parseFloat(productPrice.replace(/(\d+),(\d+).*/, '$1.$2'))
                        }
                    ]
                });
            },
            (response) => {
                this.items = saveCopy;
                this.loading = false;

                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    addDiscountCode(code) {
        const deferred = this.$q.defer();
        this.loading = true;

        this.$http.post(
            '/ajax/checkout/discount-code',
            {code}
        ).then(
            (response) => {
                this.loading = false;

                if (response.data.message && response.data.message.type != 'success') {

                    const message = response.data.message.content;

                    this.SnackbarService.add(message, 'error', true, 3000);

                    deferred.reject(message);

                    return;
                }

                const data = response.data.data;
                this.summary = data.summary;

                deferred.resolve({
                    items: this.items,
                    summary: this.summary
                });
            },
            (response) => {
                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    removeDiscountCode(discountCode) {
        const deferred = this.$q.defer();
        this.loading = true;

        this.$http.post(
            '/ajax/checkout/delete-discount-code',
            {
                code: discountCode
            }
        ).then(
            (response) => {
                this.loading = false;

                if (!response.data.data) {
                    this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                    deferred.reject('Wystąpił nieoczekiwany błąd!');

                    return;
                }

                const data = response.data.data;
                this.summary = data.summary;

                deferred.resolve({
                    items: this.items,
                    summary: this.summary
                });
            },
            (response) => {
                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    setPayment(payment) {
        const deferred = this.$q.defer();
        this.loading = true;
        this.summary.payment_id = payment.id;

        this.$http.post(
            '/ajax/checkout/select-payment',
            {
                paymentId: payment.id
            }
        ).then(
            (response) => {
                this.loading = false;

                if (!response.data.data) {
                    this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                    deferred.reject('Wystąpił nieoczekiwany błąd!');

                    return;
                }

                const data = response.data.data;
                this.summary = data.summary;

                deferred.resolve({
                    summary: this.summary
                });
            },
            (response) => {
                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    setShipment(shipment, options = null) {
        const deferred = this.$q.defer();
        this.loading = true;
        this.summary.shipment_id = shipment.id;

        this.$http.post(
            '/ajax/checkout/select-shipment',
            {
                shipmentId: shipment.id,
                options: options
            }
        ).then(
            (response) => {
                this.loading = false;

                if (!response.data.data) {
                    this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                    deferred.reject('Wystąpił nieoczekiwany błąd!');

                    return;
                }

                const data = response.data.data;

                this.summary = data.summary;

                if (shipment.module_name == 'paczkomaty') {
                    this.shipmentOptions.paczkomaty = options;
                }

                deferred.resolve({
                    summary: this.summary
                });
            },
            (response) => {
                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    loadInvoiceData() {
        const deferred = this.$q.defer();

        this.data.gusInvoiceData = {
            companyName: null,
            address: null,
            zipCode: null,
            city: null,
            country: null,
            taxNumber: null
        };

        this.loadingInvoiceData = true;
        this.loadedInvoiceDataError = null;

        this.$http.get(`/ajax/users/getGusReportByTaxId/${this.data.invoiceAddress.taxNumber}`).then(
            (response) => {
                this.loadingInvoiceData = false;

                if (response.data && response.data.message && response.data.message.type == 'error') {
                    this.loadedInvoiceDataError = response.data.message.content;

                    deferred.reject(response.data.message.content);

                    return;
                }

                if (!response.data.data) {
                    this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                    deferred.reject('Wystąpił nieoczekiwany błąd!');

                    return;
                }

                this.data.gusInvoiceData = response.data.data;

                this.data.invoiceAddress.taxNumber = this.data.gusInvoiceData.taxNumber;

                deferred.resolve(this.data.gusInvoiceData);
            },
            (response) => {
                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    finalizeOrder() {
        if (!this.stepTwoComplete) return;

        const deferred = this.$q.defer();

        this.loading = true;

        this.$http.post(
            '/ajax/checkout/set-addresses-and-finalize-order',
            this.data
        ).then(
            (response) => {
                this.loading = false;

                if (response.data && response.data.message && response.data.message.type == 'error') {
                    this.SnackbarService.add(response.data.message.content, 'error', true, 15000);

                    deferred.reject('Błędne dane!');

                    return;
                }

                if (!response.data.data) {
                    this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                    deferred.reject('Wystąpił nieoczekiwany błąd!');

                    return;
                }

                const orderId = response.data.data.orderId;

                this.orderId = orderId;
                this.payment = response.data.data.payment;
                this.userEmail = response.data.data.userEmail;

                deferred.resolve({ orderId });
            },
            (response) => {
                this.SnackbarService.add('Wystąpił nieoczekiwany błąd!', 'error', true, 3000);

                deferred.reject('Wystąpił nieoczekiwany błąd!');
            }
        );

        return deferred.promise;
    }

    /* State walidators */

    get stepOneComplete() {
        if (!this.loaded) return false;

        if (this.loading) return false;

        if (!this.summary) return false;

        if (!this.summary.payment_id) return false;

        if (!this.summary.shipment_id) return false;

        if (!this.shipments) return false;

        const shipment = this.shipments.filter(shipment => shipment.id == this.summary.shipment_id)[0];

        if (!shipment) return false;

        if (shipment.module_name && shipment.module_name == 'paczkomaty') {
            if (!this.shipmentOptions) return false;
            if (!this.shipmentOptions.paczkomaty) return false;
            if (!this.shipmentOptions.paczkomaty.selected) return false;
        }

        return true;
    }

    get stepTwoComplete() {
        if (!this.data.acceptTerms) {
            return false;
        }

        if (this.userLogged) {
            for (const validatorFnName in this.loggedDataValidations) {
                if (!this.loggedDataValidations[validatorFnName]()) {
                    return false;
                }
            }
        } else {
            for (const validatorFnName in this.dataValidations) {
                if (!this.dataValidations[validatorFnName]()) {
                    return false;
                }
            }
        }

        return true;
    }

    get stepTwoValidationErrors() {
        let errors = {};

        if (!this.data.acceptTerms) {
            errors.acceptTerms = true;
        }

        if (this.userLogged) {
            for (const validatorFnName in this.loggedDataValidations) {
                if (!this.loggedDataValidations[validatorFnName]()) {
                    errors[validatorFnName] = true;
                }
            }
        } else {
            for (const validatorFnName in this.dataValidations) {
                if (!this.dataValidations[validatorFnName]()) {
                    errors[validatorFnName] = true;
                }
            }
        }

        return errors;
    }

    get orderFinalized() {
        if (this.orderId) return true;

        return false;
    }

    updateInvoiceSelection(type) {
        if (type === 'invoice' && this.data[type]) {
            this.data.invoice_natural_person = false;
            this.data.invoice_vat_ue = false;
        }

        if (type === 'invoice_vat_ue' && this.data[type]) {
            this.data.invoice_natural_person = false;
            this.data.invoice = false;
        }

        if (type === 'invoice_natural_person' && this.data[type]) {
            this.data.invoice = false;
            this.data.invoice_vat_ue = false;
        }
    }
}

export default CartService;